import React, { useState, useEffect } from 'react';
import youtubeChannelService from '../../services/youtubeChannelService';
import ChannelVideoList from './ChannelVideoList';

function ImportFromChannelSection({ galleryId, onImportComplete }) {
  const [channelInput, setChannelInput] = useState('');
  const [isApiConfigured, setIsApiConfigured] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState('');
  const [channelData, setChannelData] = useState(null);
  const [videos, setVideos] = useState([]);
  const [selectedVideoIds, setSelectedVideoIds] = useState(new Set());
  const [nextPageToken, setNextPageToken] = useState(null);
  const [totalResults, setTotalResults] = useState(0);
  const [importResult, setImportResult] = useState(null);

  // Check API status on mount
  useEffect(() => {
    const checkApiStatus = async () => {
      try {
        const status = await youtubeChannelService.getApiStatus();
        setIsApiConfigured(status.isConfigured);
      } catch (err) {
        setIsApiConfigured(false);
      }
    };
    checkApiStatus();
  }, []);

  const handleSearch = async () => {
    if (!channelInput.trim()) {
      setError('Please enter a channel name or URL');
      return;
    }

    setLoading(true);
    setError('');
    setChannelData(null);
    setVideos([]);
    setSelectedVideoIds(new Set());
    setImportResult(null);

    try {
      const result = await youtubeChannelService.previewChannel(channelInput.trim());
      setChannelData(result.channel);
      setVideos(result.videos);
      setNextPageToken(result.nextPageToken);
      setTotalResults(result.totalResults);
      // Select all videos by default
      setSelectedVideoIds(new Set(result.videos.map(v => v.videoId)));
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to find channel. Please check the name or URL.');
    } finally {
      setLoading(false);
    }
  };

  const handleLoadMore = async () => {
    if (!nextPageToken || !channelData) return;

    setLoadingMore(true);
    try {
      const result = await youtubeChannelService.loadMoreVideos(channelData.channelId, nextPageToken);
      const newVideos = result.videos;
      setVideos([...videos, ...newVideos]);
      setNextPageToken(result.nextPageToken);
      // Also select new videos by default
      setSelectedVideoIds(prev => {
        const updated = new Set(prev);
        newVideos.forEach(v => updated.add(v.videoId));
        return updated;
      });
    } catch (err) {
      setError('Failed to load more videos');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleToggleVideo = (videoId) => {
    setSelectedVideoIds(prev => {
      const updated = new Set(prev);
      if (updated.has(videoId)) {
        updated.delete(videoId);
      } else {
        updated.add(videoId);
      }
      return updated;
    });
  };

  const handleSelectAll = () => {
    setSelectedVideoIds(new Set(videos.map(v => v.videoId)));
  };

  const handleDeselectAll = () => {
    setSelectedVideoIds(new Set());
  };

  const handleImport = async () => {
    const selectedVideos = videos.filter(v => selectedVideoIds.has(v.videoId));
    if (selectedVideos.length === 0) {
      setError('Please select at least one video to import');
      return;
    }

    setImporting(true);
    setError('');

    try {
      const videosToImport = selectedVideos.map(v => ({
        videoUrl: v.videoUrl,
        title: v.title,
        thumbnailUrl: v.thumbnailUrl,
        description: v.description
      }));

      const result = await youtubeChannelService.importVideos(galleryId, videosToImport);
      setImportResult(result);

      if (result.successCount > 0 && onImportComplete) {
        onImportComplete(result.importedVideos);
      }

      // Clear state after successful import
      if (result.failedCount === 0) {
        setChannelData(null);
        setVideos([]);
        setSelectedVideoIds(new Set());
        setChannelInput('');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to import videos');
    } finally {
      setImporting(false);
    }
  };

  const handleReset = () => {
    setChannelData(null);
    setVideos([]);
    setSelectedVideoIds(new Set());
    setChannelInput('');
    setError('');
    setImportResult(null);
  };

  if (isApiConfigured === null) {
    return (
      <div className="border-t pt-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Import from YouTube Channel</h2>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isApiConfigured === false) {
    return (
      <div className="border-t pt-8">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Import from YouTube Channel</h2>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <svg className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <p className="text-sm text-yellow-800 font-medium">YouTube API not configured</p>
              <p className="text-sm text-yellow-700 mt-1">
                To import videos from YouTube channels, a YouTube Data API key needs to be configured by the administrator.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border-t pt-8">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Import from YouTube Channel</h2>

      <div className="bg-gray-50 rounded-lg p-4">
        {/* Search Input */}
        <div className="flex gap-3 mb-4">
          <input
            type="text"
            value={channelInput}
            onChange={(e) => setChannelInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Channel name, @handle, or URL..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            disabled={loading || importing}
          />
          <button
            onClick={handleSearch}
            disabled={loading || importing || !channelInput.trim()}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium flex items-center gap-2 transition-colors"
          >
            {loading ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Searching...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                Search
              </>
            )}
          </button>
        </div>

        {/* Help text */}
        {!channelData && !loading && (
          <p className="text-sm text-gray-500 mb-4">
            Enter a YouTube channel name, @handle (e.g., @MrBeast), or full channel URL
          </p>
        )}

        {/* Error Message */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}

        {/* Import Result */}
        {importResult && (
          <div className={`mb-4 px-4 py-3 rounded-lg text-sm ${
            importResult.failedCount === 0
              ? 'bg-green-50 border border-green-200 text-green-700'
              : 'bg-yellow-50 border border-yellow-200 text-yellow-700'
          }`}>
            <p className="font-medium">
              {importResult.successCount} video{importResult.successCount !== 1 ? 's' : ''} imported successfully
              {importResult.failedCount > 0 && `, ${importResult.failedCount} failed`}
            </p>
            {importResult.errors?.length > 0 && (
              <ul className="mt-2 text-xs list-disc list-inside">
                {importResult.errors.map((err, i) => (
                  <li key={i}>{err}</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Channel Info & Videos */}
        {channelData && (
          <div className="border border-gray-200 rounded-lg bg-white">
            {/* Channel Header */}
            <div className="p-4 border-b border-gray-200 flex items-center gap-4">
              {channelData.thumbnailUrl && (
                <img
                  src={channelData.thumbnailUrl}
                  alt={channelData.title}
                  className="w-12 h-12 rounded-full object-cover"
                />
              )}
              <div className="flex-1">
                <h3 className="font-semibold text-gray-900">{channelData.title}</h3>
                <p className="text-sm text-gray-500">
                  {totalResults} video{totalResults !== 1 ? 's' : ''} available
                </p>
              </div>
              <button
                onClick={handleReset}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                title="Clear"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Video List */}
            <ChannelVideoList
              videos={videos}
              selectedVideoIds={selectedVideoIds}
              onToggleVideo={handleToggleVideo}
              onSelectAll={handleSelectAll}
              onDeselectAll={handleDeselectAll}
            />

            {/* Load More */}
            {nextPageToken && (
              <div className="p-4 border-t border-gray-200 text-center">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="px-4 py-2 text-primary-600 hover:text-primary-700 font-medium disabled:text-gray-400"
                >
                  {loadingMore ? 'Loading...' : `Load More Videos (${videos.length} of ${totalResults})`}
                </button>
              </div>
            )}

            {/* Import Button */}
            <div className="p-4 border-t border-gray-200 flex justify-end">
              <button
                onClick={handleImport}
                disabled={importing || selectedVideoIds.size === 0}
                className="px-6 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 disabled:bg-gray-400 disabled:cursor-not-allowed font-medium flex items-center gap-2 transition-colors"
              >
                {importing ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Importing...
                  </>
                ) : (
                  <>
                    Add {selectedVideoIds.size} to gallery
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  </>
                )}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ImportFromChannelSection;
